"use client";

import { faChevronDown, faChevronUp, faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  Label as AriaLabel,
  Group,
  Input,
  NumberField,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";

import type { ReactNode } from "react";
import type { NumberFieldProps } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";

// NumberInput - A number input allows users to enter and adjust numeric values.
//
// Built on React Aria Components NumberField:
// https://react-spectrum.adobe.com/react-aria/NumberField.html

const numberInputVariants = tv({
  slots: {
    base: "group flex flex-col gap-1",
    label: "text-sm font-semibold text-gray-900 dark:text-gray-100",
    group: [
      "relative",
      "w-full",
      "inline-flex",
      "tap-highlight-transparent",
      "flex-row",
      "items-center",
      "overflow-hidden",
      "px-3",
      "gap-3",
      "border",
      "border-solid",
      "border-gray-150",
      "dark:border-gray-700",
      "bg-white",
      "dark:bg-gray-800",
      "rounded",
      "hover:border-gray-300",
      "dark:hover:border-gray-600",
      "focus-within:border-blue-400",
      "dark:focus-within:border-blue-700",
    ],
    input: [
      "h-full",
      "w-full",
      "flex-1",
      "!border-none",
      "bg-transparent",
      "font-normal",
      "!outline-none",
      "placeholder:text-gray-300",
      "focus-visible:outline-none",
      "text-gray-850",
      "dark:text-white",
      "dark:caret-white",
      "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
      "data-[has-prefix=true]:ps-1.5",
      "data-[has-suffix=true]:pe-1.5",
    ],
    stepperContainer: "-mr-3 flex flex-col self-stretch",
    stepButton: [
      "flex flex-1 items-center justify-center",
      "cursor-pointer appearance-none border-none p-0 outline-none",
      "bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600",
      "transition-colors",
      "disabled:cursor-not-allowed disabled:opacity-50",
    ],
    inlineStepButton: [
      "flex items-center justify-center self-stretch",
      "cursor-pointer appearance-none border-none p-0 outline-none",
      "bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600",
      "transition-colors",
      "disabled:cursor-not-allowed disabled:opacity-50",
    ],
    prefix: "-ml-1 mr-1 text-sm text-gray-500 dark:text-gray-400",
    suffix: "-mr-1 ml-1 text-sm text-gray-500 dark:text-gray-400",
    description: "text-xs text-gray-400",
    errorMessage: "text-xs text-red",
  },
  variants: {
    size: {
      sm: {
        group: "h-7 min-h-7 px-2",
        input: "text-xs",
        stepperContainer: "-mr-2",
        stepButton: "w-6",
        inlineStepButton: "w-7",
      },
      md: {
        group: "h-8 min-h-8",
        input: "text-sm",
        stepButton: "w-7",
        inlineStepButton: "w-8",
      },
      lg: {
        group: "h-10 min-h-10 rounded-md",
        input: "text-base",
        stepButton: "w-8",
        inlineStepButton: "w-10",
      },
    },
    isDisabled: {
      true: {
        base: "pointer-events-none opacity-60",
        group: "pointer-events-none",
        input: "cursor-not-allowed",
      },
    },
    isInvalid: {
      true: {
        label: "!text-red",
        input: "!placeholder:text-red !text-red",
        group: "!border-red focus-within:border-red",
      },
    },
    stepperStyle: {
      stacked: {},
      inline: {
        stepperContainer: "flex-row border-l-0",
      },
      none: {},
    },
  },
  defaultVariants: {
    size: "md",
    isDisabled: false,
    isInvalid: false,
    stepperStyle: "stacked",
  },
});

export interface NumberInputProps
  extends Omit<NumberFieldProps, "children" | "className">,
    VariantProps<typeof numberInputVariants> {
  /** Label text for the number input */
  label?: string;
  /** Description text shown below the input */
  description?: string;
  /** Error message shown when invalid */
  errorMessage?: string;
  /** Custom class names for different slots */
  classNames?: Partial<{
    base: string;
    label: string;
    group: string;
    input: string;
    stepButton: string;
    prefix: string;
    suffix: string;
    description: string;
    errorMessage: string;
  }>;
  /** Content to show before the input (e.g., currency symbol) */
  prefix?: ReactNode;
  /** Content to show after the input (e.g., unit) */
  suffix?: ReactNode;
  /** Whether to show increment/decrement buttons */
  showStepper?: boolean;
  /** Style of the stepper buttons: 'stacked' (up/down), 'inline' (+/-), or 'none' */
  stepperStyle?: "stacked" | "inline" | "none";
  /**
   * Callback fired when the value changes.
   * Alias for onChange for antd compatibility.
   */
  onValueChange?: (value: number) => void;
  /**
   * Alias for minValue to match antd API.
   */
  min?: number;
  /**
   * Alias for maxValue to match antd API.
   */
  max?: number;
  /**
   * Alias for isDisabled to match antd API.
   */
  disabled?: boolean;
  /**
   * Format function for displaying the value.
   * Note: React Aria's NumberField uses formatOptions for locale-aware formatting.
   * This prop provides a simpler custom format option for antd compatibility.
   */
  formatter?: (value: number | undefined) => string;
  /**
   * Parse function to convert string input back to number.
   * Used with formatter for custom display formats.
   */
  parser?: (value: string | undefined) => number;
  /** Base className for the component */
  className?: string;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      classNames,
      label,
      description,
      errorMessage,
      prefix,
      suffix,
      showStepper = true,
      stepperStyle: stepperStyleProp = "stacked",
      size,
      isDisabled: isDisabledProp,
      disabled,
      isInvalid: isInvalidProp,
      minValue: minValueProp,
      maxValue: maxValueProp,
      min,
      max,
      step = 1,
      value,
      defaultValue,
      onChange,
      onValueChange,
      onBlur,
      formatOptions,
      formatter,
      parser,
      ...props
    },
    ref
  ) => {
    const isDisabled = isDisabledProp ?? disabled ?? false;
    const isInvalid = isInvalidProp ?? !!errorMessage;
    const minValue = minValueProp ?? min;
    const maxValue = maxValueProp ?? max;
    const stepperStyle = showStepper ? stepperStyleProp : "none";

    const slots = useMemo(
      () => numberInputVariants({ size, isDisabled, isInvalid, stepperStyle }),
      [size, isDisabled, isInvalid, stepperStyle]
    );

    // For custom formatter/parser, we need to use controlled input mode
    const useCustomFormatting = !!(formatter || parser);

    // Track the displayed value for custom formatting mode
    const [displayValue, setDisplayValue] = useState<string>(() => {
      const initialValue = value ?? defaultValue;
      if (useCustomFormatting && formatter && initialValue !== undefined) {
        return formatter(initialValue);
      }
      return initialValue?.toString() ?? "";
    });

    // Track if we're in edit mode (user is typing)
    const isEditingRef = useRef(false);

    // Update display value when value prop changes (for custom formatting mode)
    useEffect(() => {
      if (useCustomFormatting && value !== undefined && !isEditingRef.current) {
        setDisplayValue(formatter ? formatter(value) : value.toString());
      }
    }, [value, formatter, useCustomFormatting]);

    // Handle value changes
    const handleChange = useCallback(
      (newValue: number) => {
        onChange?.(newValue);
        onValueChange?.(newValue);
      },
      [onChange, onValueChange]
    );

    // Handle input changes for custom formatting mode
    const handleCustomInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        setDisplayValue(rawValue);
        isEditingRef.current = true;

        // Parse the value and notify
        const parsedValue = parser ? parser(rawValue) : parseFloat(rawValue);
        if (!isNaN(parsedValue)) {
          // Apply min/max constraints
          let constrainedValue = parsedValue;
          if (minValue !== undefined && constrainedValue < minValue) {
            constrainedValue = minValue;
          }
          if (maxValue !== undefined && constrainedValue > maxValue) {
            constrainedValue = maxValue;
          }
          handleChange(constrainedValue);
        }
      },
      [parser, minValue, maxValue, handleChange]
    );

    // Handle blur for custom formatting mode
    const handleCustomBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        isEditingRef.current = false;
        // Reformat the display value on blur
        if (formatter && value !== undefined) {
          setDisplayValue(formatter(value));
        }
        // Cast to Element type for compatibility with NumberFieldProps.onBlur
        onBlur?.(e as unknown as React.FocusEvent<Element>);
      },
      [formatter, value, onBlur]
    );

    // Handle focus: keep custom-formatted text when a formatter is provided so
    // display (e.g. "85%") does not jump to the underlying number ("0.85").
    const handleCustomFocus = useCallback(() => {
      isEditingRef.current = true;
      if (value !== undefined) {
        setDisplayValue(formatter ? formatter(value) : value.toString());
      }
    }, [value, formatter]);

    // Handle stepper clicks for custom formatting mode
    const handleIncrement = useCallback(() => {
      const currentValue = value ?? defaultValue ?? 0;
      let newValue = currentValue + step;
      if (maxValue !== undefined && newValue > maxValue) {
        newValue = maxValue;
      }
      handleChange(newValue);
      if (formatter) {
        setDisplayValue(formatter(newValue));
      }
    }, [value, defaultValue, step, maxValue, handleChange, formatter]);

    const handleDecrement = useCallback(() => {
      const currentValue = value ?? defaultValue ?? 0;
      let newValue = currentValue - step;
      if (minValue !== undefined && newValue < minValue) {
        newValue = minValue;
      }
      handleChange(newValue);
      if (formatter) {
        setDisplayValue(formatter(newValue));
      }
    }, [value, defaultValue, step, minValue, handleChange, formatter]);

    // Internal ref for the input
    const inputRef = useRef<HTMLInputElement>(null);

    // Combine refs
    useEffect(() => {
      if (ref) {
        if (typeof ref === "function") {
          ref(inputRef.current);
        } else {
          ref.current = inputRef.current;
        }
      }
    }, [ref]);

    // Render custom formatting version
    if (useCustomFormatting) {
      return (
        <div className={cn(slots.base(), classNames?.base, className)}>
          {label && <label className={cn(slots.label(), classNames?.label)}>{label}</label>}
          <div className={cn(slots.group(), classNames?.group)}>
            {stepperStyle === "inline" && (
              <button
                type="button"
                onClick={handleDecrement}
                disabled={isDisabled || (minValue !== undefined && (value ?? 0) <= minValue)}
                className={cn(slots.inlineStepButton(), "-ml-3", classNames?.stepButton)}
              >
                <FontAwesomeIcon icon={faMinus} className="size-3" />
              </button>
            )}
            {prefix && <span className={cn(slots.prefix(), classNames?.prefix)}>{prefix}</span>}
            <input
              ref={inputRef}
              type="text"
              value={displayValue}
              onChange={handleCustomInputChange}
              onBlur={handleCustomBlur}
              onFocus={handleCustomFocus}
              disabled={isDisabled}
              data-has-prefix={!!prefix}
              data-has-suffix={!!suffix}
              className={cn(slots.input(), classNames?.input)}
            />
            {suffix && <span className={cn(slots.suffix(), classNames?.suffix)}>{suffix}</span>}
            {stepperStyle === "inline" && (
              <button
                type="button"
                onClick={handleIncrement}
                disabled={isDisabled || (maxValue !== undefined && (value ?? 0) >= maxValue)}
                className={cn(slots.inlineStepButton(), "-mr-3", classNames?.stepButton)}
              >
                <FontAwesomeIcon icon={faPlus} className="size-3" />
              </button>
            )}
            {stepperStyle === "stacked" && (
              <div className={cn(slots.stepperContainer())}>
                <button
                  type="button"
                  onClick={handleIncrement}
                  disabled={isDisabled || (maxValue !== undefined && (value ?? 0) >= maxValue)}
                  className={cn(slots.stepButton(), classNames?.stepButton)}
                >
                  <FontAwesomeIcon icon={faChevronUp} className="size-2.5" />
                </button>
                <button
                  type="button"
                  onClick={handleDecrement}
                  disabled={isDisabled || (minValue !== undefined && (value ?? 0) <= minValue)}
                  className={cn(slots.stepButton(), classNames?.stepButton)}
                >
                  <FontAwesomeIcon icon={faChevronDown} className="size-2.5" />
                </button>
              </div>
            )}
          </div>
          {description && !errorMessage && (
            <p className={cn(slots.description(), classNames?.description)}>{description}</p>
          )}
          {errorMessage && (
            <p className={cn(slots.errorMessage(), classNames?.errorMessage)}>{errorMessage}</p>
          )}
        </div>
      );
    }

    // Default: use React Aria NumberField
    return (
      <NumberField
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        formatOptions={formatOptions}
        className={cn(slots.base(), classNames?.base, className)}
        {...props}
      >
        {label && <AriaLabel className={cn(slots.label(), classNames?.label)}>{label}</AriaLabel>}
        <Group className={cn(slots.group(), classNames?.group)}>
          {stepperStyle === "inline" && (
            <AriaButton
              slot="decrement"
              className={cn(slots.inlineStepButton(), "-ml-3", classNames?.stepButton)}
            >
              <FontAwesomeIcon icon={faMinus} className="size-3" />
            </AriaButton>
          )}
          {prefix && <span className={cn(slots.prefix(), classNames?.prefix)}>{prefix}</span>}
          <Input
            ref={ref}
            data-has-prefix={!!prefix}
            data-has-suffix={!!suffix}
            className={cn(slots.input(), classNames?.input)}
            onBlur={onBlur}
          />
          {suffix && <span className={cn(slots.suffix(), classNames?.suffix)}>{suffix}</span>}
          {stepperStyle === "inline" && (
            <AriaButton
              slot="increment"
              className={cn(slots.inlineStepButton(), "-mr-3", classNames?.stepButton)}
            >
              <FontAwesomeIcon icon={faPlus} className="size-3" />
            </AriaButton>
          )}
          {stepperStyle === "stacked" && (
            <div className={cn(slots.stepperContainer())}>
              <AriaButton
                slot="increment"
                className={cn(slots.stepButton(), classNames?.stepButton)}
              >
                <FontAwesomeIcon icon={faChevronUp} className="size-2.5" />
              </AriaButton>
              <AriaButton
                slot="decrement"
                className={cn(slots.stepButton(), classNames?.stepButton)}
              >
                <FontAwesomeIcon icon={faChevronDown} className="size-2.5" />
              </AriaButton>
            </div>
          )}
        </Group>
        {description && !errorMessage && (
          <p className={cn(slots.description(), classNames?.description)}>{description}</p>
        )}
        {errorMessage && (
          <p className={cn(slots.errorMessage(), classNames?.errorMessage)}>{errorMessage}</p>
        )}
      </NumberField>
    );
  }
);
NumberInput.displayName = "NumberInput";
